import { useEffect, useState } from 'react';
import { getPendingFile } from '../../store/quotationDraftStore';
import type { QuotationDraft } from '../../types/quotation.types';
import { guessPreviewKind } from '../../utils/previewKind';
import { EmailReviewSourcePanel } from './EmailReviewSourcePanel';
import { ManualUploadReviewSourcePanel } from './ManualUploadReviewSourcePanel';
import { ReviewImageViewer } from './ReviewImageViewer';
import { ReviewPdfViewer } from './ReviewPdfViewer';
import { ReviewSpreadsheetViewer } from './ReviewSpreadsheetViewer';
import { ReviewSpreadsheetGridViewer } from './ReviewSpreadsheetGridViewer';
import { ReviewTextViewer } from './ReviewTextViewer';
import { ReviewUnavailableState } from './ReviewUnavailableState';

interface ReviewSourcePanelProps {
  draft: QuotationDraft;
}

export function ReviewSourcePanel({ draft }: ReviewSourcePanelProps) {
  // ⚠️ EVERY HOOK RUNS BEFORE THE EARLY RETURNS BELOW, and that ordering is the whole point.
  //
  // Until 2026-08-18 the two hooks sat AFTER the `sourceEmail` and `attachments` early returns, so the
  // number of hooks this component called depended on the draft it was rendering. React tracks hooks
  // positionally, so a draft that gained `serverId` (a save completing) or `attachments` (a fetch landing)
  // while this panel was mounted changed the hook count between two renders of the SAME component — which
  // is a hard crash, "Rendered fewer hooks than expected", not a degraded render.
  //
  // It shipped because `npm run lint` had never run: there was no eslint.config.js and ESLint 9 ignores
  // .eslintrc, so `react-hooks/rules-of-hooks` had nothing to fire on. Found the hour the config was added.
  const file = draft.extractionSourceKind === 'file' ? getPendingFile(draft.id) : null;

  // Create the blob URL inside useEffect (not useMemo) so StrictMode's double-invoked
  // cleanup revokes only the URL it created on the same pass — never the live one
  // that's currently in the DOM.
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file) {
      setObjectUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setObjectUrl(url);
    return () => {
      URL.revokeObjectURL(url);
      setObjectUrl((current) => (current === url ? null : current));
    };
  }, [file]);

  // Email-sourced drafts (AutoDrafts created by CSC-280) get the tabbed email/attachments
  // viewer instead of the single-source viewer. Falls back to the manual-flow path below
  // for drafts that originated from a paste/upload.
  if (draft.sourceEmail) {
    return <EmailReviewSourcePanel sourceEmail={draft.sourceEmail} />;
  }

  // Manually-uploaded multi-file drafts (CSC-283 Layer 3): bytes are server-stored under
  // Quotation.Attachments. Render the lightweight attachment-pill panel — no in-memory
  // File is available since the user reached this draft via navigation, not the local
  // upload session.
  if (draft.attachments.length > 0 && draft.serverId != null) {
    return (
      <ManualUploadReviewSourcePanel
        quotationId={draft.serverId}
        attachments={draft.attachments}
      />
    );
  }

  const fileName = draft.extractionFileName ?? file?.name ?? '';
  const kind = guessPreviewKind(fileName);

  if (file && objectUrl && kind === 'image') {
    return <ReviewImageViewer src={objectUrl} alt={fileName} />;
  }
  if (file && objectUrl && kind === 'pdf') {
    return <ReviewPdfViewer src={objectUrl} fileName={fileName} />;
  }
  // Real grid render when the binary is in session — falls back to the extracted-text
  // viewer if SheetJS can't parse the file (corrupt / unsupported format / etc.).
  if (file && kind === 'spreadsheet') {
    return (
      <ReviewSpreadsheetGridViewer
        file={file}
        fileName={fileName}
        fallback={
          draft.extractionText ? (
            <ReviewSpreadsheetViewer text={draft.extractionText} fileName={fileName} />
          ) : undefined
        }
      />
    );
  }
  // Reloaded session — no binary, fall back to extracted text representation.
  if (kind === 'spreadsheet' && draft.extractionText) {
    return <ReviewSpreadsheetViewer text={draft.extractionText} fileName={fileName} />;
  }
  if (draft.extractionSourceKind === 'text' && draft.extractionText) {
    return <ReviewTextViewer text={draft.extractionText} />;
  }

  return <ReviewUnavailableState fileName={draft.extractionFileName ?? null} />;
}
